/* eslint-disable max-classes-per-file */

export const DEVICE_MODE = Object.freeze({
  MOUSE: 'MOUSE',
  TOUCHPAD: 'TOUCHPAD',
});

class PointerDeviceDetectorByScroll {
  get FIRST_WHEEL_DELTA_MOUSE() {
    return this.bowser.safari
      ? PointerDeviceDetectorByScroll.FIRST_WHEEL_DELTA_MOUSE_SAFARI_IOS
      : PointerDeviceDetectorByScroll.FIRST_WHEEL_DELTA_MOUSE_CHROME_IOS;
  }

  constructor(state, bowser, loggingEnabled = false) {
    this.bowser = bowser;
    this.firstWheelDeltaYInTimeFrame = null;
    this.endTimeFrame = this.endTimeFrameHandler.bind(this);
    this.state = state;
    this.loggingEnabled = loggingEnabled;
  }

  log(...msg) {
    if (this.loggingEnabled) {
      console.log('[DetectorByScroll]', ...msg);
    }
  }

  destroy() {
    clearTimeout(this.checkTimeFrameEndedID);
  }

  changeMode(e) {
    this.startTimeFrame(e);
    const mode = this.getModeByWheelEvent(e);
    this.state.setMode(mode);
  }

  startTimeFrame() {
    this.startFrameTime = Date.now();
    clearTimeout(this.checkTimeFrameEndedID);
    this.checkTimeFrameEndedID = setTimeout(
      this.endTimeFrame,
      PointerDeviceDetectorByScroll.EVENTS_MEASUREMENT_TIME_FRAME
    );
  }

  endTimeFrameHandler() {
    this.startFrameTime = 0;
    this.firstWheelDeltaYInTimeFrame = null;
  }

  getModeByWheelEvent(e) {
    const mode = this.state.getMode();
    this.log(`CURRENT MODE ${mode}`);
    switch (mode) {
      case DEVICE_MODE.MOUSE:
        return this.detectTouchpad(e) ? DEVICE_MODE.TOUCHPAD : mode;
      case DEVICE_MODE.TOUCHPAD:
        return this.detectMouse(e) ? DEVICE_MODE.MOUSE : mode;
      default:
        return mode;
    }
  }

  detectMouse(e) {
    this.log(e);
    if (this.bowser.firefox && e.deltaMode === WheelEvent.DOM_DELTA_LINE) {
      this.log('[detectMouse]', 1);
      return true;
    }
    if (this.isPinchZoom(e)) {
      this.log('[detectMouse]', 2);
      return false;
    }
    if (
      this.bowser.mac &&
      e.wheelDeltaY !== undefined &&
      e.wheelDeltaY !== null &&
      this.firstWheelDeltaYInTimeFrame === null
    ) {
      this.firstWheelDeltaYInTimeFrame = Math.abs(e.wheelDeltaY);
      this.log(this.firstWheelDeltaYInTimeFrame);
      this.log(this.FIRST_WHEEL_DELTA_MOUSE);

      const result =
        this.firstWheelDeltaYInTimeFrame === this.FIRST_WHEEL_DELTA_MOUSE;
      this.log('[detectMouse]', 3, result);
      return result;
    }
    this.log('[detectMouse]', 4);
    return false;
  }

  detectTouchpad(e) {
    if (this.bowser.firefox && e.deltaMode !== WheelEvent.DOM_DELTA_LINE) {
      this.log('[detectTouchpad]', 'firefox');
      return true;
    }
    if (this.bowser.safari) {
      if (e.scale && Number.isFinite(e.scale)) {
        this.log('[detectTouchpad]', 'safari && isFinite(e.scale)');
        return true;
      }
    }
    if (this.isPinchZoom(e)) {
      this.log('[detectTouchpad]', 'isPinchZoom');
      return true;
    }
    if (e.deltaX === 0 || e.shiftKey) {
      this.log(
        '[detectTouchpad]',
        'e.deltaX = ',
        e.deltaX,
        'e.shiftKey = ',
        e.shiftKey
      );
      return false;
    }
    this.log('[detectTouchpad]', 'default');
    return true;
  }

  isPinchZoom(e) {
    if (!e.metaKey && !e.ctrlKey) {
      return false;
    }
    if (this.bowser.windows) {
      const { wheelDelta, deltaY } = e;
      return (
        wheelDelta === undefined ||
        (deltaY !== undefined && Math.abs(deltaY) < 100)
      );
    }
    return true;
  }
}

PointerDeviceDetectorByScroll.EVENTS_MEASUREMENT_TIME_FRAME = 100;
PointerDeviceDetectorByScroll.FIRST_WHEEL_DELTA_MOUSE_CHROME_IOS = 120;
PointerDeviceDetectorByScroll.FIRST_WHEEL_DELTA_MOUSE_SAFARI_IOS = 12;

class PointerDeviceDetectorByClick {
  constructor(state) {
    this.state = state;
  }

  destroy() {}

  changeMode(e) {
    if (e.buttons === 4) {
      this.state.setMode(DEVICE_MODE.MOUSE);
    }
  }
}

export class PointerDeviceDetector {
  constructor({
    bowser,
    currentMode,
    parentElement,
    canvasElement,
    onModeChange,
    logging = false,
  }) {
    this.onWheel = this.onWheelEventHandler.bind(this);
    this.onCanvasMove = this.onCanvasMoveEventHandler.bind(this);

    this.bowser = bowser;
    this.parentElement = parentElement;
    this.canvasElement = canvasElement;

    this.autoDetect = true;

    const that = this;
    const state = {
      currentMode,
      getMode() {
        return this.currentMode;
      },
      setMode(mode, silent = false) {
        that.log('SETTING MODE', mode);
        if (mode !== this.getMode()) {
          const prevMode = this.getMode();
          this.currentMode = mode;
          if (!silent && onModeChange) {
            onModeChange(mode, prevMode);
          }
        }
      },
    };
    this.state = state;
    this.loggingEnabled = logging;
    this.detectorByScroll = new PointerDeviceDetectorByScroll(
      this.state,
      bowser,
      this.loggingEnabled
    );
    this.detectorByClick = new PointerDeviceDetectorByClick(
      this.state,
      this.loggingEnabled
    );
    this.initListeners();
  }

  log(...msg) {
    if (this.loggingEnabled) {
      console.log(`[PointerDeviceDetector]`, ...msg);
    }
  }

  getMode() {
    return this.state.getMode();
  }

  setMode(mode, silent = false) {
    this.state.setMode(mode, silent);
  }

  destroy() {
    this.detectorByClick.destroy();
    this.detectorByScroll.destroy();

    this.parentElement.removeEventListener(
      PointerDeviceDetector.WHEEL_EVENT_NAME,
      this.onWheel,
      { capture: true, passive: true }
    );
    if (this.bowser.safari && this.bowser.desktop) {
      document.body.removeEventListener('gesturestart', this.onWheel, true);
      document.body.removeEventListener('gestureend', this.onWheel, true);
      document.body.removeEventListener('gesturechange', this.onWheel, true);
    }
    this.canvasElement.removeEventListener(
      'panzoompan',
      this.onCanvasMove,
      true
    );
  }

  initListeners() {
    this.parentElement.addEventListener(
      PointerDeviceDetector.WHEEL_EVENT_NAME,
      this.onWheel,
      { capture: true, passive: true }
    );
    if (this.bowser.safari && this.bowser.desktop) {
      document.body.addEventListener('gesturestart', this.onWheel, true);
      document.body.addEventListener('gestureend', this.onWheel, true);
      document.body.addEventListener('gesturechange', this.onWheel, true);
    }
    this.canvasElement.addEventListener('panzoompan', this.onCanvasMove, true);
  }

  onWheelEventHandler(e) {
    if (this.autoDetect) {
      this.detectorByScroll.changeMode(e);
    }
  }

  onCanvasMoveEventHandler(e) {
    if (this.autoDetect) {
      if (e.detail.originalEvent) {
        this.detectorByClick.changeMode(e.detail.originalEvent);
      }
    }
  }

  isPinchZoom(e) {
    return this.detectorByScroll.isPinchZoom(e);
  }
}

// In browser mode
if (typeof window !== 'undefined') {
  PointerDeviceDetector.WHEEL_EVENT_NAME =
    'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';
}
